"use client"

import { useState, useEffect } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { signIn, useSession } from "next-auth/react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import Image from "next/image"
import { applyPlatformBrand, type PlatformProductBrand } from "@/lib/platform-products/branding"

const loginSchema = z.object({
  email: z.string().email({
    message: "Por favor, insira um email válido.",
  }),
  password: z.string().min(1, {
    message: "Por favor, insira sua senha.",
  }),
})

export default function LoginPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { data: session, status } = useSession()
  const callbackUrl = searchParams.get("callbackUrl") || "/dashboard"
  const orgId = searchParams.get("org") // Obter o ID da organização, se houver
  const hasError = searchParams.get("error") === "true"
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [brand, setBrand] = useState<PlatformProductBrand | null>(null)

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  useEffect(() => {
    if (hasError) {
      setError("Falha na autenticação. Verifique seu email e senha.")
    }
  }, [hasError])

  useEffect(() => {
    let active = true

    async function loadBrand() {
      try {
        const params = new URLSearchParams()
        if (orgId) params.set("org", orgId)
        if (typeof window !== "undefined") params.set("domain", window.location.host)

        const response = await fetch(`/api/platform-brand?${params.toString()}`)
        if (!response.ok) return

        const data = await response.json()
        if (!active || !data.product) return

        setBrand(data.product)
        applyPlatformBrand(data.product)
      } catch (error) {
        console.error("Erro ao carregar marca do login:", error)
      }
    }

    loadBrand()

    return () => {
      active = false
    }
  }, [orgId])

  useEffect(() => {
    if (status === "authenticated" && session?.user) {
      router.replace("/dashboard")
    }
  }, [status, session, router])

  async function onSubmit(values: z.infer<typeof loginSchema>) {
    setIsLoading(true)
    setError(null)

    try {
      const extraParams: Record<string, string> = {}
      if (orgId) {
        extraParams.orgId = orgId
      }

      const result = await signIn("credentials", {
        redirect: false,
        email: values.email,
        password: values.password,
        ...extraParams,
      })

      if (result?.error) {
        setError("Email ou senha inválidos. Por favor, tente novamente.")
        return
      }

      await new Promise(resolve => setTimeout(resolve, 300))
      window.location.href = callbackUrl
    } catch (error) {
      setError("Ocorreu um erro ao fazer login. Por favor, tente novamente.")
    } finally {
      setIsLoading(false)
    }
  }

  if (status === "authenticated" && session?.user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4" role="status">
        Redirecionando para o dashboard...
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <div className="flex justify-center">
            <Image
              src={
                brand?.logos?.login ||
                process.env.NEXT_PUBLIC_LOGIN_LOGO_URL ||
                process.env.NEXT_PUBLIC_LOGO_URL ||
                "/images/estilo-ai-logo.svg"
              }
              alt={brand?.name ? `${brand.name} Logo` : "SmartMedix Logo"}
              width={160}
              height={50}
              priority
            />
          </div>
          <CardDescription>Faça login para acessar {brand?.name || "o painel administrativo"}.</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }: { field: any }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input placeholder="seu@email.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }: { field: any }) => (
                  <FormItem>
                    <FormLabel>Senha</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? "Entrando..." : "Entrar"}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="flex justify-center text-sm text-muted-foreground">
          <p>Painel administrativo exclusivo para administradores</p>
        </CardFooter>
      </Card>
    </div>
  )
}
